import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { allowedBanks, bankChip, bankLabel } from '../utils/requestTheme';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import api from '../lib/api';
import { 
  FileText, Clock, CheckCircle, Package, Building2, 
  PackageCheck, PackageX, PackageMinus, Receipt, CreditCard, AlertTriangle,
  Timer, AlertCircle, Banknote
} from 'lucide-react';
import { toast } from 'sonner';
import { ActionItems } from '../components/ActionItems';


const roleMessages = {
  purchasing: 'Kelola Purchase Orders Anda',
  gudang: 'Kelola Penerimaan Barang dan Invoice',
  account_payable: 'Kelola Pembayaran Invoice',
  internal: 'Ajukan Pembayaran Internal',
  operasional: 'Ajukan Pembayaran Operasional',
  approval: 'Setujui Pembayaran yang Diajukan',
  admin: 'Kelola Sistem dan Pengguna'
};

const roleColors = {
  purchasing: 'from-blue-500 to-blue-600',
  gudang: 'from-orange-500 to-orange-600',
  account_payable: 'from-emerald-500 to-emerald-600',
  internal: 'from-purple-500 to-purple-600',
  operasional: 'from-[#800000] to-[#5c0000]',
  approval: 'from-indigo-500 to-indigo-600',
  admin: 'from-slate-500 to-slate-600'
};

// Simple bar component for chart
const Bar = ({ label, value, maxValue, color, suffix = '' }) => {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="font-medium text-slate-700 truncate max-w-[150px]" title={label}>{label}</span>
        <span className="font-mono text-slate-600">{value}{suffix}</span>
      </div>
      <div className="h-5 bg-slate-100 rounded-full overflow-hidden">
        <div 
          className={`h-full ${color} rounded-full transition-all duration-500`}
          style={{ width: `${Math.max(percentage, 2)}%` }}
        />
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [enhancedStats, setEnhancedStats] = useState(null);
  const [apDashboard, setApDashboard] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'account_payable') {
      fetchApDashboard();
    } else {
      fetchEnhancedDashboard();
    }
    if (user?.role === 'approval' || user?.role === 'admin') {
      fetchPaymentStatus();
    }
  }, [user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEnhancedDashboard = useCallback(async () => {
    try {
      const response = await api.get(`/dashboard/enhanced`);
      setEnhancedStats(response.data);
    } catch (error) {
      console.error('Failed to fetch enhanced dashboard:', error);
      toast.error('Gagal memuat dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPaymentStatus = useCallback(async () => {
    try {
      const response = await api.get(`/dashboard/payment-status`);
      setPaymentStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch payment status:', error);
    }
  }, []);

  const fetchApDashboard = useCallback(async () => {
    try {
      const response = await api.get(`/dashboard/ap`);
      setApDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch AP dashboard:', error);
      toast.error('Gagal memuat dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  // AP Dashboard
  if (user?.role === 'account_payable' && apDashboard) {
    const umurHutang = apDashboard.umur_hutang || {};
    const summary = apDashboard.summary || {};

    return (
      <Layout>
        <div data-testid="dashboard-ap" className="space-y-6">
          {/* Welcome Banner */}
          <div className={`bg-gradient-to-r ${roleColors[user?.role]} text-white rounded-lg p-6 shadow-lg`}>
            <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Selamat Datang, {user?.name}!
            </h2>
            <p className="text-emerald-50" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              {roleMessages[user?.role]}
            </p>
          </div>

          <ActionItems />

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-l-4 border-l-blue-500">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Total Invoice</CardTitle>
                <Receipt className="h-5 w-5 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {summary.total_invoices || 0}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Rp {(summary.total_amount || 0).toLocaleString('id-ID')}
                </p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-green-500">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Dalam Batas</CardTitle>
                <CheckCircle className="h-5 w-5 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {umurHutang.dalam_batas?.count || 0}
                </div>
                <p className="text-xs text-slate-500 mt-1">belum jatuh tempo (&gt; 7 hari lagi)</p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-amber-500">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Pemantauan</CardTitle>
                <Timer className="h-5 w-5 text-amber-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-amber-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {umurHutang.pemantauan?.count || 0}
                </div>
                <p className="text-xs text-slate-500 mt-1">jatuh tempo ≤ 7 hari</p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-red-500">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Lewat Batas</CardTitle>
                <AlertCircle className="h-5 w-5 text-red-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {umurHutang.lewat_batas?.count || 0}
                </div>
                <p className="text-xs text-slate-500 mt-1">lewat 1-7 hari · lewat &gt;7 hari: <b>{umurHutang.lewat_berat?.count || 0}</b></p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Proses Bayar Card */}
            <Card className="border-l-4 border-l-indigo-500">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Proses Bayar</CardTitle>
                <CreditCard className="h-5 w-5 text-indigo-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-indigo-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {summary.proses_bayar_count || 0}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Rp {(summary.proses_bayar_amount || 0).toLocaleString('id-ID')}
                </p>
              </CardContent>
            </Card>

            {/* Terbayar Card */}
            <Card className="border-l-4 border-l-emerald-500">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Terbayar</CardTitle>
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {summary.terbayar_count || 0}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Rp {(summary.terbayar_amount || 0).toLocaleString('id-ID')}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Umur Hutang Details */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Dalam Batas */}
            <Card className="border-t-4 border-t-green-500">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg text-green-700">
                  <CheckCircle className="h-5 w-5" />
                  BELUM JATUH TEMPO (&gt; 7 hari lagi)
                </CardTitle>
                <p className="text-sm text-slate-500">
                  Total: Rp {(umurHutang.dalam_batas?.total_amount || 0).toLocaleString('id-ID')}
                </p>
              </CardHeader>
              <CardContent>
                {umurHutang.dalam_batas?.data?.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {umurHutang.dalam_batas.data.map((inv, idx) => (
                      <div key={idx} className="bg-green-50 p-2 rounded text-sm border border-green-200">
                        <div className="flex justify-between">
                          <span className="font-mono font-semibold">{inv.invoice_number}</span>
                          <span className="text-green-700">{inv.umur_hari} hari</span>
                        </div>
                        <div className="text-xs text-slate-600 mt-1">{inv.supplier_name}</div>
                        <div className="text-xs font-mono text-right mt-1">Rp {inv.total_amount?.toLocaleString('id-ID')}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">Tidak ada data</p>
                )}
              </CardContent>
            </Card>

            {/* Pemantauan */}
            <Card className="border-t-4 border-t-amber-500">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg text-amber-700">
                  <Timer className="h-5 w-5" />
                  JATUH TEMPO ≤ 7 HARI
                </CardTitle>
                <p className="text-sm text-slate-500">
                  Total: Rp {(umurHutang.pemantauan?.total_amount || 0).toLocaleString('id-ID')}
                </p>
              </CardHeader>
              <CardContent>
                {umurHutang.pemantauan?.data?.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {umurHutang.pemantauan.data.map((inv, idx) => (
                      <div key={idx} className="bg-amber-50 p-2 rounded text-sm border border-amber-200">
                        <div className="flex justify-between">
                          <span className="font-mono font-semibold">{inv.invoice_number}</span>
                          <span className="text-amber-700">{inv.umur_hari} hari</span>
                        </div>
                        <div className="text-xs text-slate-600 mt-1">{inv.supplier_name}</div>
                        <div className="text-xs font-mono text-right mt-1">Rp {inv.total_amount?.toLocaleString('id-ID')}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">Tidak ada data</p>
                )}
              </CardContent>
            </Card>

            {/* Lewat Batas */}
            <Card className="border-t-4 border-t-red-500">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg text-red-700">
                  <AlertCircle className="h-5 w-5" />
                  LEWAT 1-7 HARI
                </CardTitle>
                <p className="text-sm text-slate-500">
                  Total: Rp {(umurHutang.lewat_batas?.total_amount || 0).toLocaleString('id-ID')}
                </p>
              </CardHeader>
              <CardContent>
                {umurHutang.lewat_batas?.data?.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {umurHutang.lewat_batas.data.map((inv, idx) => (
                      <div key={idx} className="bg-red-50 p-2 rounded text-sm border border-red-200">
                        <div className="flex justify-between">
                          <span className="font-mono font-semibold">{inv.invoice_number}</span>
                          <span className="text-red-700 font-bold">{inv.umur_hari} hari</span>
                        </div>
                        <div className="text-xs text-slate-600 mt-1">{inv.supplier_name}</div>
                        <div className="text-xs font-mono text-right mt-1">Rp {inv.total_amount?.toLocaleString('id-ID')}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">Tidak ada data</p>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border-t-4 border-t-red-800" data-testid="ap-lewat-berat">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg text-red-900">
                  <AlertCircle className="h-5 w-5" /> LEWAT &gt; 7 HARI
                </CardTitle>
                <p className="text-sm text-slate-500">Total: Rp {(umurHutang.lewat_berat?.total_amount || 0).toLocaleString('id-ID')}</p>
              </CardHeader>
              <CardContent>
                {umurHutang.lewat_berat?.data?.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {umurHutang.lewat_berat.data.map((inv, idx) => (
                      <div key={idx} className="bg-red-100 p-2 rounded text-sm border border-red-300">
                        <div className="flex justify-between">
                          <span className="font-mono font-semibold">{inv.invoice_number}</span>
                          <span className="text-red-900 font-bold">lewat {-inv.sisa_hari} hari</span>
                        </div>
                        <div className="text-xs text-slate-600 mt-1">{inv.supplier_name} · jatuh tempo {new Date(inv.due_date).toLocaleDateString('id-ID')}</div>
                        <div className="text-xs font-mono text-right mt-1">Rp {inv.total_amount?.toLocaleString('id-ID')}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">Tidak ada data</p>
                )}
              </CardContent>
            </Card>
            <Card className="border-t-4 border-t-amber-400" data-testid="ap-minggu-ini">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg text-amber-800">
                  <Timer className="h-5 w-5" /> JATUH TEMPO MINGGU INI
                </CardTitle>
                <p className="text-sm text-slate-500">{(apDashboard.jatuh_tempo_minggu_ini || []).length} invoice dalam 7 hari ke depan</p>
              </CardHeader>
              <CardContent>
                {(apDashboard.jatuh_tempo_minggu_ini || []).length > 0 ? (
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs text-slate-500"><th className="py-1">Jatuh Tempo</th><th>Invoice</th><th>Supplier</th><th className="text-right">Nilai</th></tr></thead>
                    <tbody>
                      {apDashboard.jatuh_tempo_minggu_ini.map((inv) => (
                        <tr key={inv.id} className="border-t">
                          <td className="py-1 font-mono text-xs">{new Date(inv.due_date).toLocaleDateString('id-ID')} <span className="text-amber-700">({inv.sisa_hari === 0 ? 'hari ini' : `${inv.sisa_hari} hr`})</span></td>
                          <td className="font-mono text-xs">{inv.invoice_number}</td>
                          <td className="text-xs">{inv.supplier_name}</td>
                          <td className="text-right font-mono text-xs">Rp {inv.total_amount?.toLocaleString('id-ID')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-slate-500 text-center py-4">Tidak ada invoice jatuh tempo minggu ini</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  // Regular Dashboard (Purchasing, Gudang, Admin)
  const stats = enhancedStats?.status_counts || {};
  const apStats = enhancedStats?.ap_stats || {};
  const poBySupplier = enhancedStats?.po_by_supplier || [];
  const poByCompany = enhancedStats?.po_by_company || [];
  const warnings = enhancedStats?.warnings || { draft: [], submitted: [] };

  const maxSupplierCount = Math.max(...poBySupplier.map(s => s.count), 1);
  const maxCompanyAmount = Math.max(...poByCompany.map(c => c.amount), 1);

  return (
    <Layout>
      <div data-testid="dashboard" className="space-y-6">
        {/* Warnings Section */}
        {(warnings.draft.length > 0 || warnings.submitted.length > 0) && (
          <div className="space-y-3">
            {warnings.draft.length > 0 && (
              <Card className="border-l-4 border-l-amber-500 bg-amber-50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="h-5 w-5" />
                    Peringatan: PO Draft &gt; 2 Minggu ({warnings.draft.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-amber-100">
                          <th className="text-left p-2">No. PO</th>
                          <th className="text-left p-2">Tanggal</th>
                          <th className="text-left p-2">Supplier</th>
                          <th className="text-right p-2">Total</th>
                          <th className="text-center p-2">Interval</th>
                        </tr>
                      </thead>
                      <tbody>
                        {warnings.draft.map((po, idx) => (
                          <tr key={idx} className="border-b border-amber-200 hover:bg-amber-100 cursor-pointer" onClick={() => navigate('/purchase-orders')}>
                            <td className="p-2 font-mono">{po.po_number}</td>
                            <td className="p-2">{po.po_date}</td>
                            <td className="p-2">{po.supplier_name}</td>
                            <td className="p-2 text-right font-mono">Rp {po.total_amount?.toLocaleString('id-ID')}</td>
                            <td className="p-2 text-center">
                              <span className={`px-2 py-0.5 rounded text-xs ${po.interval === '> 4 Minggu' ? 'bg-red-200 text-red-700' : 'bg-amber-200 text-amber-700'}`}>
                                {po.interval}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {warnings.submitted.length > 0 && (
              <Card className="border-l-4 border-l-orange-500 bg-orange-50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-orange-700">
                    <AlertTriangle className="h-5 w-5" />
                    Peringatan: PO Submitted &gt; 2 Minggu ({warnings.submitted.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-orange-100">
                          <th className="text-left p-2">No. PO</th>
                          <th className="text-left p-2">Tanggal</th>
                          <th className="text-left p-2">Supplier</th>
                          <th className="text-right p-2">Total</th>
                          <th className="text-center p-2">Interval</th>
                        </tr>
                      </thead>
                      <tbody>
                        {warnings.submitted.map((po, idx) => (
                          <tr key={idx} className="border-b border-orange-200 hover:bg-orange-100 cursor-pointer" onClick={() => navigate('/purchase-orders')}>
                            <td className="p-2 font-mono">{po.po_number}</td>
                            <td className="p-2">{po.po_date}</td>
                            <td className="p-2">{po.supplier_name}</td>
                            <td className="p-2 text-right font-mono">Rp {po.total_amount?.toLocaleString('id-ID')}</td>
                            <td className="p-2 text-center">
                              <span className={`px-2 py-0.5 rounded text-xs ${po.interval === '> 4 Minggu' ? 'bg-red-200 text-red-700' : 'bg-orange-200 text-orange-700'}`}>
                                {po.interval}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Welcome Banner */}
        <div className={`bg-gradient-to-r ${roleColors[user?.role]} text-white rounded-lg p-6 shadow-lg`}>
          <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Selamat Datang, {user?.name}!
          </h2>
          <p className="text-blue-50" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            {roleMessages[user?.role]}
          </p>
        </div>

        <ActionItems />

        {/* PO Status Cards Row 1 */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Card 
            className="border-l-4 border-l-blue-500 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/purchase-orders')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 pt-3 px-3">
              <CardTitle className="text-xs font-medium text-slate-600">Total PO</CardTitle>
              <FileText className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent className="pb-3 px-3">
              <div className="text-xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {stats.total || 0}
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-l-4 border-l-slate-400 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/purchase-orders?status=draft')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 pt-3 px-3">
              <CardTitle className="text-xs font-medium text-slate-600">Draft</CardTitle>
              <Clock className="h-4 w-4 text-slate-500" />
            </CardHeader>
            <CardContent className="pb-3 px-3">
              <div className="text-xl font-bold text-slate-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {stats.draft || 0}
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-l-4 border-l-green-500 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/purchase-orders?status=diterima')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 pt-3 px-3">
              <CardTitle className="text-xs font-medium text-slate-600">Diterima</CardTitle>
              <PackageCheck className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent className="pb-3 px-3">
              <div className="text-xl font-bold text-green-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {stats.diterima || 0}
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-l-4 border-l-yellow-500 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/purchase-orders?status=diterima_adj')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 pt-3 px-3">
              <CardTitle className="text-xs font-medium text-slate-600">Diterima Adj</CardTitle>
              <Package className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent className="pb-3 px-3">
              <div className="text-xl font-bold text-yellow-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {stats.diterima_adj || 0}
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-l-4 border-l-orange-500 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/purchase-orders?status=diterima_partial')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 pt-3 px-3">
              <CardTitle className="text-xs font-medium text-slate-600">Partial</CardTitle>
              <PackageMinus className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent className="pb-3 px-3">
              <div className="text-xl font-bold text-orange-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {stats.diterima_partial || 0}
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-l-4 border-l-red-500 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/purchase-orders?status=reject')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 pt-3 px-3">
              <CardTitle className="text-xs font-medium text-slate-600">Reject</CardTitle>
              <PackageX className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent className="pb-3 px-3">
              <div className="text-xl font-bold text-red-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {stats.reject || 0}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* AP Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-l-4 border-l-amber-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Submit ke AP</CardTitle>
              <Receipt className="h-5 w-5 text-amber-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {apStats.submitted_to_ap || 0}
              </div>
              <p className="text-xs text-slate-500 mt-1">Invoice menunggu diproses</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-indigo-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Diproses</CardTitle>
              <Clock className="h-5 w-5 text-indigo-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-indigo-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {apStats.processed || 0}
              </div>
              <p className="text-xs text-slate-500 mt-1">Sedang diproses AP</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-emerald-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Dibayarkan</CardTitle>
              <CreditCard className="h-5 w-5 text-emerald-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {apStats.paid || 0}
              </div>
              <p className="text-xs text-slate-500 mt-1">Pembayaran selesai</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Section - Top 10 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* PO by Supplier Chart (Top 10) */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Package className="h-5 w-5 text-blue-600" />
                PO per Supplier (Top 10)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {poBySupplier.length > 0 ? (
                <div className="space-y-3">
                  {poBySupplier.map((supplier, index) => (
                    <Bar 
                      key={supplier.name}
                      label={supplier.name}
                      value={supplier.count}
                      maxValue={maxSupplierCount}
                      color={`bg-blue-${Math.max(500 - index * 50, 100)}`}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">Belum ada data</p>
              )}
            </CardContent>
          </Card>

          {/* PO by Company Chart (Top 10) */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building2 className="h-5 w-5 text-emerald-600" />
                Nilai PO per Perusahaan (Top 10)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {poByCompany.length > 0 ? (
                <div className="space-y-3">
                  {poByCompany.map((company, index) => (
                    <div key={company.name} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium text-slate-700 truncate max-w-[120px]" title={company.name}>{company.name}</span>
                        <span className="font-mono text-slate-600 text-xs">Rp {(company.amount / 1000000).toFixed(1)}M</span>
                      </div>
                      <div className="h-5 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full bg-emerald-${Math.max(500 - index * 50, 100)} rounded-full transition-all duration-500`}
                          style={{ width: `${Math.max((company.amount / maxCompanyAmount) * 100, 2)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">Belum ada data</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Payment Status Section for Approval/Admin */}
        {paymentStatus && (user?.role === 'approval' || user?.role === 'admin') && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Status Pembayaran
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-amber-50 border-amber-200">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-amber-700">Siap Proses</CardTitle>
                  <Clock className="h-5 w-5 text-amber-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-amber-700" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {paymentStatus.bank_process?.siap_proses || 0}
                  </div>
                  <p className="text-xs text-amber-600 mt-1">
                    Rp {((paymentStatus.bank_process?.siap_proses_amount || 0) / 1000000).toFixed(1)}M
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-blue-50 border-blue-200">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-blue-700">Diproses Bank</CardTitle>
                  <Timer className="h-5 w-5 text-blue-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-700" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {paymentStatus.bank_process?.diproses_bank || 0}
                  </div>
                  <p className="text-xs text-blue-600 mt-1">
                    Rp {((paymentStatus.bank_process?.diproses_bank_amount || 0) / 1000000).toFixed(1)}M
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-emerald-50 border-emerald-200">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-emerald-700">Dibayarkan</CardTitle>
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-emerald-700" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {paymentStatus.bank_process?.dibayarkan || 0}
                  </div>
                  <p className="text-xs text-emerald-600 mt-1">
                    Rp {((paymentStatus.bank_process?.dibayarkan_amount || 0) / 1000000).toFixed(1)}M
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* By Bank — hanya antrean dalam cakupan approval */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-testid="dashboard-by-bank">
              {allowedBanks(user).map((bt) => {
                const d = paymentStatus.by_bank?.[bt] || {};
                return (
                  <Card key={bt} data-testid={`dashboard-bank-${bt.toLowerCase()}`}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-bold ${bankChip(bt)}`}>{bankLabel(bt)}</span>
                        {bt.startsWith('BSI') ? 'Bank Syariah Indonesia' : 'Bank Rakyat Indonesia'}{bt.endsWith('_OPS') ? ' — Operasional' : ''}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex justify-around text-center">
                        <div>
                          <div className="text-lg font-bold text-amber-600">{d.siap_proses || 0}</div>
                          <div className="text-xs text-slate-500">Siap</div>
                        </div>
                        <div>
                          <div className="text-lg font-bold text-blue-600">{d.diproses_bank || 0}</div>
                          <div className="text-xs text-slate-500">Proses</div>
                        </div>
                        <div>
                          <div className="text-lg font-bold text-emerald-600">{d.dibayarkan || 0}</div>
                          <div className="text-xs text-slate-500">Selesai</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}



